import React, { useMemo, useState } from 'react';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { Phone, Star, ChevronDown, Search } from 'lucide-react';
import { Card, Section } from './primitives';

/*
 * Contacts tab driven by data:
 *   groups: [{ id, title, open?: boolean, items: [ContactItem] }]
 *   ContactItem: { label, phone, note? }                      -> one tap-to-call row
 *              | { label, phones: [{ label, phone }] }        -> one row, several numbers (e.g. OR rooms)
 * Favourites live on the device under `storageKey` (and in the profile when signed in) and pin to the top.
 * Groups with `open: true` are always expanded; the rest collapse by default.
 */

const normalize = (s) => s.toLowerCase();

const ContactRow = ({ item, accent, isFavorite, onToggleFavorite }) => {
  if (item.phones) {
    return (
      <div className="flex items-center gap-3 px-4 py-3">
        <Phone size={18} style={{ color: accent }} className="flex-shrink-0" />
        <div className="flex-1 min-w-0 text-[15px] font-medium text-gray-900 dark:text-white">{item.label}</div>
        <div className="flex gap-1.5">
          {item.phones.map((p) => (
            <a
              key={p.phone}
              href={`tel:${p.phone}`}
              onClick={() => {}}
              className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
            >
              {p.label}
            </a>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-stretch">
      <a
        href={`tel:${item.phone}`}
        onClick={() => {}}
        className="flex-1 min-w-0 flex items-center gap-3 pl-4 py-3"
      >
        <Phone size={18} style={{ color: accent }} className="flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-[15px] font-medium text-gray-900 dark:text-white leading-snug">{item.label}</div>
          {item.note && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{item.note}</div>}
        </div>
      </a>
      {onToggleFavorite && (
        <button
          onClick={() => { onToggleFavorite(item.phone); }}
          aria-label={isFavorite ? 'Remove from favourites' : 'Add to favourites'}
          className="px-4 flex items-center text-gray-300 dark:text-gray-600"
        >
          <Star size={18} className={isFavorite ? 'fill-amber-400 text-amber-400' : ''} />
        </button>
      )}
    </div>
  );
};

/** Always-open group: its own section and card */
const OpenGroup = ({ group, accent, favorites, onToggleFavorite }) => (
  <Section title={group.title}>
    <Card>
      {group.items.map((item) => (
        <ContactRow
          key={item.phone || item.label}
          item={item}
          accent={accent}
          isFavorite={favorites.includes(item.phone)}
          onToggleFavorite={item.phones ? null : onToggleFavorite}
        />
      ))}
    </Card>
  </Section>
);

/**
 * The remaining groups share a card of rows. Tapping a row opens that group in
 * place as its own labelled card (like the always-open groups); the rows after
 * it continue in a card below. The chevron in the opened label folds it back.
 */
const MoreGroups = ({ groups, accent, favorites, onToggleFavorite }) => {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (id) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Split into runs of collapsed rows and opened groups, in order
  const segments = [];
  groups.forEach((g) => {
    if (open.has(g.id)) segments.push({ type: 'group', group: g });
    else if (segments.length && segments[segments.length - 1].type === 'rows') segments[segments.length - 1].groups.push(g);
    else segments.push({ type: 'rows', groups: [g] });
  });

  return (
    <>
      {segments.map((seg, i) =>
        seg.type === 'group' ? (
          <Section
            key={seg.group.id}
            title={seg.group.title}
            aside={
              <button onClick={() => toggle(seg.group.id)} aria-label={`Collapse ${seg.group.title}`} className="p-1 -m-1 text-gray-400">
                <ChevronDown size={16} className="rotate-180" />
              </button>
            }
          >
            <Card>
              {seg.group.items.map((item) => (
                <ContactRow
                  key={item.phone || item.label}
                  item={item}
                  accent={accent}
                  isFavorite={favorites.includes(item.phone)}
                  onToggleFavorite={item.phones ? null : onToggleFavorite}
                />
              ))}
            </Card>
          </Section>
        ) : (
          <Section key={`rows-${i}`} title={i === 0 ? 'More' : undefined}>
            <Card>
              {seg.groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => toggle(group.id)}
                  aria-expanded={false}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left"
                >
                  <span className="flex-1 text-[15px] font-medium text-gray-900 dark:text-white">{group.title}</span>
                  <span className="text-xs text-gray-400 dark:text-gray-500 tabular-nums">{group.items.length}</span>
                  <ChevronDown size={18} className="text-gray-300 dark:text-gray-600" />
                </button>
              ))}
            </Card>
          </Section>
        )
      )}
    </>
  );
};

const ContactList = ({ groups, accent, storageKey }) => {
  const [query, setQuery] = useState('');
  const [favorites, setFavorites] = useLocalStorage(storageKey, []);

  const toggleFavorite = (phone) =>
    setFavorites((prev) => (prev.includes(phone) ? prev.filter((p) => p !== phone) : [...prev, phone]));

  const allItems = useMemo(
    () => groups.flatMap((g) => g.items.flatMap((i) => (i.phones ? i.phones.map((p) => ({ label: `${i.label} · ${p.label}`, phone: p.phone })) : [i]))),
    [groups]
  );
  const results = query.trim()
    ? allItems.filter((i) => normalize(i.label).includes(normalize(query)) || normalize(i.note || '').includes(normalize(query)))
    : null;
  const favoriteItems = allItems.filter((i) => favorites.includes(i.phone));

  return (
    <div className="space-y-5">
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          placeholder="Search contacts"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 text-[15px] rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': accent }}
        />
      </div>

      {results ? (
        <Section title={`${results.length} result${results.length === 1 ? '' : 's'}`}>
          {results.length > 0 && (
            <Card>
              {results.map((item) => (
                <ContactRow key={item.phone} item={item} accent={accent} isFavorite={favorites.includes(item.phone)} onToggleFavorite={toggleFavorite} />
              ))}
            </Card>
          )}
        </Section>
      ) : (
        <>
          {favoriteItems.length > 0 && (
            <Section title="Favourites">
              <Card>
                {favoriteItems.map((item) => (
                  <ContactRow key={item.phone} item={item} accent={accent} isFavorite onToggleFavorite={toggleFavorite} />
                ))}
              </Card>
            </Section>
          )}
          {groups.filter((g) => g.open).map((group) => (
            <OpenGroup key={group.id} group={group} accent={accent} favorites={favorites} onToggleFavorite={toggleFavorite} />
          ))}
          {groups.some((g) => !g.open) && (
            <MoreGroups groups={groups.filter((g) => !g.open)} accent={accent} favorites={favorites} onToggleFavorite={toggleFavorite} />
          )}
        </>
      )}
    </div>
  );
};

export default ContactList;
